<template>
  <div class="page-background" :style="backgroundStyle">
    <picture v-if="imageProps">
      <source
        v-if="desktopImageProps"
        media="(min-width: 700px)"
        :srcset="desktopImageProps.imgAttrs.srcset"
        :sizes="desktopImageProps.imgAttrs.sizes"
      />
      <img
        ref="imgRef"
        v-bind="imageProps.imgAttrs"
        alt=""
        class="page-background__img"
        :class="{ 'is-loaded': imageLoaded }"
        :style="imgStyle"
        data-allow-mismatch="class"
        onload="this.classList.add('is-loaded')"
        @load="imageLoaded = true"
      />
    </picture>
    <div
      v-if="overlayBg"
      class="page-background__overlay"
      :style="{ backgroundColor: overlayBg }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useImagePreset } from '@render/composables/useImagePreset'
import {
  getColorStyleData,
  getGradientStyleData,
  getSizeStyleData,
} from '@render/composables/useStyleData'
import type { PageBackgroundFields } from './definition'
import { useDocumentBackground } from './documentBackground'

const props = withDefaults(
  defineProps<{
    fields?: PageBackgroundFields
  }>(),
  {
    fields: () => ({}),
  },
)

const imgRef = ref<HTMLImageElement>()
const imageLoaded = ref(false)
const imageProps = useImagePreset(() => props.fields?.image)
const desktopImageProps = useImagePreset(() => props.fields?.desktopImage)

// The <img>'s static `onload` shows a prerendered background as soon as its
// file arrives, without waiting for the app; this catches `imageLoaded` up with
// it. Same mechanism and reasons as PlainImage's.
onMounted(() => {
  if (imgRef.value?.complete && imgRef.value.naturalWidth > 0) {
    imageLoaded.value = true
  }
})

// Multi-source watcher: each getter is tracked and compared independently.
// `watch(() => [a, b], …)` (single getter returning an array) would fire on
// every reactive trigger because Vue reference-compares the returned array;
// fresh arrays are never equal, so the callback would run on every parent
// schema rebroadcast and reset imageLoaded mid-life — fading the image out.
watch([() => props.fields?.image?.id, () => props.fields?.desktopImage?.id], () => {
  imageLoaded.value = false
})

const backgroundStyle = computed(() => {
  const s: Record<string, string> = {}
  const bgColor = getColorStyleData(props.fields?.bgColor)
  if (bgColor) s.backgroundColor = bgColor
  const gradient = getGradientStyleData(props.fields?.bgGradient)
  if (gradient) s.backgroundImage = gradient
  const opacity = props.fields?.opacity
  if (opacity != null && opacity !== 1) s['--img-opacity'] = String(opacity)
  // Default -10 (rather than -1) leaves room for intermediate background
  // layers (e.g. a StickyContainer in fix-to-page mode at z-index -5).
  s.zIndex = String(props.fields?.zIndex ?? -10)
  return s
})

const imgStyle = computed(() => {
  const s: Record<string, string> = {}
  s.objectFit = props.fields?.objectFit || 'cover'
  s.objectPosition = props.fields?.objectPosition || 'center'

  if (props.fields?.fadeIn !== false) {
    const duration = getSizeStyleData(props.fields?.fadeDuration) || '0.4s'
    s.transitionDuration = duration
  }

  return s
})

const overlayBg = computed(() => getColorStyleData(props.fields?.overlayColor))

// Tints the browser chrome (see documentBackground.ts). A gradient has no single
// color, so its first stop stands in.
useDocumentBackground(() => {
  const bgColor = getColorStyleData(props.fields?.bgColor)
  if (bgColor) return bgColor
  const gradient = props.fields?.bgGradient
  if (gradient?.type !== 'linear_gradient') return undefined
  const [first] = [...(gradient.stops ?? [])].sort((a, b) => a.position - b.position)
  return getColorStyleData(first?.color)
})
</script>

<style scoped>
.page-background {
  position: fixed;
  inset: 0;
  pointer-events: none;
  /* z-index is set inline via backgroundStyle (default -10). */
}

.page-background__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition-property: opacity;
  transition-timing-function: ease-in;
}

.page-background__img.is-loaded {
  opacity: var(--img-opacity, 1);
}

.page-background__overlay {
  position: absolute;
  inset: 0;
}
</style>
